import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Calculator, TrendingUp, Calendar, AlertTriangle, CheckCircle,
  ArrowLeft, ExternalLink, History, Settings, RefreshCw, Trash2,
  Info, Euro, Percent
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URSSAFPage = () => {
  const [taux, setTaux] = useState(null);
  const [config, setConfig] = useState(null);
  const [echeances, setEcheances] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Simulation form
  const [ca, setCa] = useState('');
  const [typeActivite, setTypeActivite] = useState('prestation_services_bnc');
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [tauxRes, configRes, echeancesRes, historiqueRes, estimationRes] = await Promise.all([
        fetch(`${API_URL}/api/urssaf/taux`),
        fetch(`${API_URL}/api/urssaf/config`, { headers }),
        fetch(`${API_URL}/api/urssaf/echeances`, { headers }),
        fetch(`${API_URL}/api/urssaf/historique?limit=10`, { headers }),
        fetch(`${API_URL}/api/urssaf/estimation-annuelle`, { headers })
      ]);

      const tauxData = await tauxRes.json();
      const configData = await configRes.json();
      const echeancesData = await echeancesRes.json();
      const historiqueData = await historiqueRes.json();
      const estimationData = await estimationRes.json();

      setTaux(tauxData);
      setConfig(configData.config);
      setTypeActivite(configData.config?.type_activite || 'prestation_services_bnc');
      setEcheances(echeancesData.echeances || []);
      setHistorique(historiqueData.simulations || []);
      setEstimation(estimationData);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSimulation = async () => {
    if (!ca || parseFloat(ca) <= 0) return;
    
    setSimulating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/urssaf/simuler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chiffre_affaires: parseFloat(ca),
          type_activite: typeActivite,
          periode: 'trimestre',
          include_cfp: true
        })
      });

      const result = await res.json();
      setSimulationResult(result);
      await loadData(); // Refresh historique
    } catch (error) {
      console.error('Erreur simulation:', error);
    } finally {
      setSimulating(false);
    }
  };

  const deleteSimulation = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/urssaf/historique/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="urssaf-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Module URSSAF</h1>
            <p className="text-slate-500 text-sm">Simulateur de cotisations sociales</p>
          </div>
        </div>
        <a 
          href="https://www.autoentrepreneur.urssaf.fr" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button variant="outline" className="gap-2">
            <ExternalLink className="h-4 w-4" />
            Portail URSSAF
          </Button>
        </a>
      </div>

      {/* Estimation annuelle */}
      {estimation && (
        <Card className={`border-2 ${estimation.alerte_plafond ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50'}`}>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${estimation.alerte_plafond ? 'bg-amber-100' : 'bg-blue-100'}`}>
                  <TrendingUp className={`h-6 w-6 ${estimation.alerte_plafond ? 'text-amber-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Estimation annuelle {estimation.annee}</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {estimation.ca_actuel?.toLocaleString('fr-FR')} € HT
                  </p>
                  <p className="text-sm text-slate-500">
                    Projection fin d'année: {estimation.ca_projete_fin_annee?.toLocaleString('fr-FR')} €
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <p className="text-sm text-slate-600">Cotisations estimées</p>
                <p className="text-xl font-bold text-violet-600">
                  {estimation.cotisations_projetees?.toLocaleString('fr-FR')} €
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${estimation.pourcentage_plafond > 90 ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(estimation.pourcentage_plafond, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500">
                    {estimation.pourcentage_plafond}% du plafond
                  </span>
                </div>
              </div>
            </div>
            {estimation.alerte_plafond && (
              <div className="mt-4 p-3 bg-amber-100 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-amber-800">
                  Attention: Vous approchez du plafond de {estimation.plafond_ca?.toLocaleString('fr-FR')} €
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Simulateur */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-100">
                <Calculator className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <CardTitle>Simulateur de cotisations</CardTitle>
                <CardDescription>Calculez vos charges sociales</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Type d'activité */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Type d'activité
              </label>
              <select
                value={typeActivite}
                onChange={(e) => setTypeActivite(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                data-testid="activity-type-select"
              >
                {taux && Object.entries(taux.taux_cotisations).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label} ({value.taux}%)
                  </option>
                ))}
              </select>
            </div>

            {/* Chiffre d'affaires */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Chiffre d'affaires à déclarer (€)
              </label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  value={ca}
                  onChange={(e) => setCa(e.target.value)}
                  placeholder="Ex: 3000"
                  className="pl-10"
                  data-testid="ca-input"
                />
              </div>
            </div>

            {/* Bouton simuler */}
            <Button 
              className="w-full bg-violet-600 hover:bg-violet-700"
              onClick={handleSimulation}
              disabled={simulating || !ca}
              data-testid="simulate-btn"
            >
              {simulating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculer mes cotisations
                </>
              )}
            </Button>

            {/* Résultat */}
            {simulationResult && (
              <div className="mt-4 p-4 bg-violet-50 rounded-xl space-y-3" data-testid="simulation-result">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Chiffre d'affaires</span>
                  <span className="font-semibold text-slate-800">
                    {simulationResult.chiffre_affaires?.toLocaleString('fr-FR')} €
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">
                    Cotisations ({simulationResult.taux_cotisations}%)
                  </span>
                  <span className="font-semibold text-violet-600">
                    - {simulationResult.montant_cotisations?.toLocaleString('fr-FR')} €
                  </span>
                </div>
                {simulationResult.montant_cfp > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">
                      CFP ({simulationResult.taux_cfp}%)
                    </span>
                    <span className="font-semibold text-violet-600">
                      - {simulationResult.montant_cfp?.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="font-medium text-slate-800">Total à payer</span>
                  <span className="text-xl font-bold text-violet-600">
                    {simulationResult.total_a_payer?.toLocaleString('fr-FR')} €
                  </span>
                </div>
                <div className="flex justify-between items-center bg-emerald-50 p-2 rounded-lg">
                  <span className="text-emerald-700">Net après charges</span>
                  <span className="font-bold text-emerald-600">
                    {simulationResult.net_apres_charges?.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Échéances */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle>Prochaines échéances</CardTitle>
                <CardDescription>Dates de déclaration URSSAF</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {echeances.map((ech, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-xl border ${
                    ech.urgence === 'critique' ? 'border-red-200 bg-red-50' :
                    ech.urgence === 'urgent' ? 'border-amber-200 bg-amber-50' :
                    'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          {ech.trimestre || ech.mois}
                        </span>
                        <Badge 
                          className={
                            ech.urgence === 'critique' ? 'bg-red-100 text-red-700' :
                            ech.urgence === 'urgent' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {ech.jours_restants === 0 ? "Aujourd'hui !" :
                           ech.jours_restants === 1 ? "Demain" :
                           `J-${ech.jours_restants}`}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{ech.periode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-800">{ech.date_limite}</p>
                      <a 
                        href={ech.lien_declaration}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 justify-end mt-1"
                      >
                        Déclarer <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Historique */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <History className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <CardTitle>Historique des simulations</CardTitle>
                <CardDescription>Vos dernières estimations</CardDescription>
              </div>
            </div>
            <Badge variant="outline">{historique.length} simulation(s)</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {historique.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {historique.map((sim) => (
                <div key={sim.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-slate-800">
                      {sim.chiffre_affaires?.toLocaleString('fr-FR')} € HT
                    </p>
                    <p className="text-sm text-slate-500">{sim.type_activite_label}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-violet-600">
                        {sim.total_a_payer?.toLocaleString('fr-FR')} € de charges
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(sim.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSimulation(sim.id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calculator className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune simulation effectuée</p>
              <p className="text-sm text-slate-400">Utilisez le simulateur ci-dessus</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-800">Note importante</h3>
              <p className="text-sm text-blue-600 mt-1">
                Ce simulateur fournit une estimation indicative basée sur les taux en vigueur en 2025. 
                Il ne remplace pas la déclaration officielle sur le site de l'URSSAF. 
                Pour toute question, contactez l'URSSAF au <strong>3957</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default URSSAFPage;
